import apiClient from '@/lib/axios';
import type {
  JobListItemDto,
  JobDetailDto,
  JobFormData,
  JobStatusUpdateDto,
  JobsQueryParams,
  ApiResponse,
  SearchJobsResponse,
} from '../types';

/**
 * Transforms form data for API submission.
 * Converts empty strings to null for optional fields.
 */
const prepareJobDataForApi = (data: JobFormData): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(data)) {
    // Convert empty strings to null for optional fields
    if (value === '') {
      result[key] = null;
    } else {
      result[key] = value;
    }
  }

  return result;
};

/**
 * Get list of jobs with optional filters
 */
export const getJobs = async (params?: JobsQueryParams): Promise<JobListItemDto[]> => {
  const response = await apiClient.get<SearchJobsResponse>('/jobs', { params });
  return response.data.items;
};

/**
 * Get a single job by ID with full details
 */
export const getJob = async (id: number): Promise<JobDetailDto> => {
  const response = await apiClient.get<JobDetailDto>(`/jobs/${id}`);
  return response.data;
};

/**
 * Get all jobs for a specific lead
 */
export const getJobsByLeadId = async (leadId: number): Promise<JobListItemDto[]> => {
  const response = await apiClient.get<JobListItemDto[]>(`/jobs/by-lead/${leadId}`);
  return response.data;
};

/**
 * Update an existing job (description and notes)
 */
export const updateJob = async (id: number, data: JobFormData): Promise<JobDetailDto> => {
  const apiData = prepareJobDataForApi(data);
  const response = await apiClient.put<ApiResponse<JobDetailDto>>(`/jobs/${id}`, apiData);
  return response.data.data;
};

/**
 * Update job status with optional closing details
 */
export const updateJobStatus = async (
  id: number,
  statusUpdate: JobStatusUpdateDto
): Promise<JobDetailDto> => {
  const response = await apiClient.put<ApiResponse<JobDetailDto>>(
    `/jobs/${id}/status`,
    statusUpdate
  );
  return response.data.data;
};
